import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useSellerAnalysisConfig, type SellerAnalysisConfig } from '../../hooks/useSellerAnalysis';
import { useSellerAutomation } from '../../hooks/useSellerAutomation';
import { api } from '../../services/api';
import { useSellerSettings } from './sellerUi';
import { AutomationRow, SaveStatus, SettingsSection, useAutoSave } from './settingsUi';

// Aba "Rotinas de anuncios": tudo que roda sozinho na tela Anuncios, no mesmo
// formato. Antes eram tres abas (Metricas, Saude, Impulsionar) cada uma com um
// jeito diferente de ligar e salvar.

type AutoSyncConfig = { enabled: boolean; intervalHours?: number; lastRunAt?: string | null };

function headers() {
  const token = localStorage.getItem('auth_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export function SellerListingsSettingsView() {
  const [sync, setSync] = useState<AutoSyncConfig | null>(null);
  const { config, saveConfig } = useSellerAnalysisConfig();
  const [analysis, setAnalysis] = useState<Pick<SellerAnalysisConfig, 'autoAnalysis' | 'autoAnalysisIntervalHours'> | null>(null);
  const { status, schedule } = useAutoSave<SellerAnalysisConfig>(saveConfig);
  const { byKey } = useSellerAutomation();
  const { openSettings } = useSellerSettings();
  const boost = byKey('boost');

  useEffect(() => {
    fetch('/api/seller/listings/auto-sync', { headers: headers() })
      .then((res) => res.json())
      .then((json) => { if (json?.success) setSync(json.config); })
      .catch(() => undefined);
  }, []);

  // So copia do servidor na primeira carga, para a resposta de um save nao
  // atropelar o que o usuario mudou enquanto ele estava em voo.
  useEffect(() => {
    if (config && !analysis) setAnalysis({ autoAnalysis: config.autoAnalysis, autoAnalysisIntervalHours: config.autoAnalysisIntervalHours });
  }, [config, analysis]);

  const toggleSync = async (enabled: boolean) => {
    const res = await fetch('/api/seller/listings/auto-sync', { method: 'PATCH', headers: headers(), body: JSON.stringify({ enabled }) });
    const json = await res.json().catch(() => ({}));
    if (!json?.success) { toast.error(json?.message || 'Falha ao salvar'); return; }
    setSync(json.config);
  };

  const patchAnalysis = (patch: Partial<SellerAnalysisConfig>, immediate = false) => {
    setAnalysis((a) => (a ? { ...a, ...patch } : a));
    schedule(patch, immediate);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><SaveStatus status={status} /></div>

      <SettingsSection title="O que roda sozinho" help="Ligue o que você quer que aconteça sem precisar clicar. Dá pra rodar tudo na mão pela tela Anúncios também.">
        {sync ? (
          <AutomationRow
            title="Atualizar métricas dos anúncios"
            help="Puxa visitas, vendas, Ads e afiliados da Shopee e do Mercado Livre. Não consome créditos."
            enabled={sync.enabled}
            onToggle={toggleSync}
            fixedFrequency="1 vez por dia"
            automationKey="listings-sync"
          />
        ) : <p className="text-xs text-gray-400">Carregando...</p>}

        {analysis ? (
          <AutomationRow
            title="Analisar anúncios com IA"
            help="A IA procura anúncios com problema e diz o que mudar. Só entram os anúncios com a IA ligada na tabela."
            enabled={analysis.autoAnalysis}
            onToggle={(on) => patchAnalysis({ autoAnalysis: on }, true)}
            hours={analysis.autoAnalysisIntervalHours}
            onHoursChange={(h) => patchAnalysis({ autoAnalysisIntervalHours: h }, true)}
            automationKey="analysis"
            credit={{ featureKey: 'listing_analysis', unitLabel: 'anúncio(s)', estimate: async () => (await api.estimateSellerAnalysis()).count }}
          />
        ) : <p className="text-xs text-gray-400">Carregando...</p>}

        {boost && (
          <div className={`flex items-center justify-between gap-3 rounded-lg border p-3.5 ${boost.enabled ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200'}`}>
            <div>
              <p className="text-sm font-medium text-gray-900">Fila de impulsionar (Shopee)</p>
              <p className="mt-0.5 text-xs text-gray-500">{boost.enabled ? 'Ligada: reimpulsiona os anúncios escolhidos a cada 4 horas.' : 'Desligada. Escolha os anúncios que entram no rodízio.'}</p>
            </div>
            <button type="button" onClick={() => openSettings('impulsionar')} className="shrink-0 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
              {boost.enabled ? 'Editar fila' : 'Montar fila'}
            </button>
          </div>
        )}
      </SettingsSection>

      <button type="button" onClick={() => openSettings('anuncios-regras')} className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-left hover:border-orange-300">
        <span className="block text-sm font-semibold text-gray-900">Regras da IA →</span>
        <span className="block text-xs text-gray-500">O que conta como problema: gasto em Ads, conversão mínima, nota mínima.</span>
      </button>
    </div>
  );
}
